import { useState, useEffect } from 'react';
import adminApi from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Heart, Trash2, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeposits();
  }, []);

  const fetchDeposits = async () => {
    try {
      const response = await adminApi.get(`/deposit-requests`);
      setDeposits(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch reservations:', error);
      }
      toast.error('Failed to load reservation requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await adminApi.put(`/deposit-requests/${id}?status=${newStatus}`);
      toast.success('Status updated!');
      fetchDeposits();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to update status:', error);
      }
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reservation request?')) return;

    try {
      await adminApi.delete(`/deposit-requests/${id}`);
      toast.success('Reservation request deleted!');
      fetchDeposits();
    } catch {
      // Silently fail
      toast.error('Failed to delete');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'contacted':
        return 'bg-blue-100 text-blue-800';
      case 'deposit_received':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-stone-100 text-stone-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'contacted':
        return 'Contacted';
      case 'deposit_received':
        return 'Confirmed';
      case 'completed':
        return 'Completed';
      default:
        return status.replace('_', ' ');
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-stone-100">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-stone-200 rounded w-1/3"></div>
          <div className="h-32 bg-stone-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-2xl border border-stone-100">
      <div className="flex items-center gap-2 mb-6">
        <Heart className="w-6 h-6 text-[#E85D75]" />
        <h2 className="text-2xl font-serif font-medium text-stone-800">Reservation Requests</h2>
        {deposits.filter(d => d.status === 'pending').length > 0 && (
          <span className="bg-[#E85D75] text-white text-xs px-2 py-1 rounded-full">
            {deposits.filter(d => d.status === 'pending').length} new
          </span>
        )}
      </div>

      <div className="space-y-4">
        {deposits.map((deposit) => (
          <div
            key={deposit.id}
            data-testid={`reservation-${deposit.id}`}
            className={`p-6 rounded-xl border ${deposit.status === 'pending' ? 'border-[#E85D75]/30 bg-rose-50/50' : 'border-stone-100 bg-stone-50'}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-stone-800">{deposit.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(deposit.status)}`}>
                    {getStatusLabel(deposit.status)}
                  </span>
                </div>
                <p className="text-[#E85D75] font-medium">
                  Interested in: {deposit.kitten_name}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-stone-400">
                  {new Date(deposit.created_at).toLocaleDateString()}
                </p>
                <p className="text-xs text-stone-400">
                  {new Date(deposit.created_at).toLocaleTimeString()}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center gap-2 text-stone-600">
                <Mail className="w-4 h-4" />
                <span className="text-sm">{deposit.email}</span>
              </div>
              <div className="flex items-center gap-2 text-stone-600">
                <Phone className="w-4 h-4" />
                <span className="text-sm">{deposit.phone}</span>
              </div>
            </div>

            {deposit.message && (
              <div className="bg-white p-3 rounded-lg mb-4">
                <p className="text-sm text-stone-600">{deposit.message}</p>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-stone-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-600">Status:</span>
                <Select
                  value={deposit.status}
                  onValueChange={(value) => handleStatusChange(deposit.id, value)}
                >
                  <SelectTrigger className="w-40 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="deposit_received">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    const subject = encodeURIComponent(`Your Kitten Reservation - ${deposit.kitten_name}`);
                    const body = encodeURIComponent(
                      `Hi ${deposit.name},\n\nThank you for your interest in ${deposit.kitten_name}!\n\nGreat news — no deposit is required! You can reserve this kitten and pay only when you pick them up.\n\nWould you like to schedule a video call to meet ${deposit.kitten_name}? We can also arrange a time for you to visit in person.\n\nPlease let us know your availability and we'll set everything up!\n\nBest regards,\nKittenState`
                    );
                    window.location.href = `mailto:${deposit.email}?subject=${subject}&body=${body}`;
                  }}
                  className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full text-sm"
                >
                  Reply to Inquiry
                </Button>
                <Button
                  onClick={() => handleDelete(deposit.id)}
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {deposits.length === 0 && (
        <div className="text-center py-12 text-stone-500">
          <Heart className="w-16 h-16 mx-auto mb-4 text-stone-200" />
          <p>No reservation requests yet.</p>
          <p className="text-sm mt-2">When customers express interest in a kitten, they'll appear here.</p>
        </div>
      )}
    </div>
  );
}
